import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState, { LoadingBlock } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

/**
 * CrudModule — config-driven CRUD page.
 *
 * fields: [{ key, label, type: 'text'|'number'|'date'|'select'|'textarea', options?, required?, defaultValue? }]
 * columns: [{ key, label, render?(row) }]
 */
export default function CrudModule({
    eyebrow, title, subtitle, testid,
    endpoint, fields, columns, emptyIcon, emptyTitle, emptyBody,
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);

    const blankForm = () => Object.fromEntries(fields.map((f) => [f.key, f.defaultValue ?? ""]));

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(endpoint);
            setItems(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

    const startCreate = () => { setEditing(null); setForm(blankForm()); setOpen(true); };
    const startEdit = (row) => {
        setEditing(row);
        const f = blankForm();
        for (const k of Object.keys(f)) f[k] = row[k] ?? "";
        setForm(f);
        setOpen(true);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        const payload = { ...form };
        for (const f of fields) {
            if (f.type === "number") {
                payload[f.key] = payload[f.key] === "" || payload[f.key] === null
                    ? 0 : Number(payload[f.key]);
            }
            if (payload[f.key] === "") payload[f.key] = null;
        }
        try {
            if (editing) {
                await api.put(`${endpoint}/${editing.id}`, payload);
                toast.success("Updated");
            } else {
                await api.post(endpoint, payload);
                toast.success("Saved");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async (row) => {
        if (!window.confirm("Delete this entry?")) return;
        await api.delete(`${endpoint}/${row.id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div data-testid={testid}>
            <PageHeader
                eyebrow={eyebrow}
                title={title}
                subtitle={subtitle}
                testid={`${testid}-header`}
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid={`${testid}-add-btn`} className="rounded-full" onClick={startCreate}>
                                <Plus className="mr-2 h-4 w-4" /> Add
                            </Button>
                        </DialogTrigger>
                        <DialogContent data-testid={`${testid}-dialog`} className="max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>{editing ? "Edit entry" : "New entry"}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={onSubmit} className="space-y-4">
                                {fields.map((f) => (
                                    <div key={f.key}>
                                        <Label htmlFor={f.key}>{f.label}</Label>
                                        {f.type === "textarea" ? (
                                            <Textarea
                                                id={f.key}
                                                data-testid={`field-${f.key}`}
                                                value={form[f.key] ?? ""}
                                                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                            />
                                        ) : f.type === "select" ? (
                                            <Select
                                                value={form[f.key] || ""}
                                                onValueChange={(v) => setForm({ ...form, [f.key]: v })}
                                            >
                                                <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                                                <SelectContent>
                                                    {f.options.map((o) => (
                                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Input
                                                id={f.key}
                                                type={f.type || "text"}
                                                required={f.required}
                                                data-testid={`field-${f.key}`}
                                                value={form[f.key] ?? ""}
                                                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                                placeholder={f.placeholder}
                                            />
                                        )}
                                    </div>
                                ))}
                                <DialogFooter>
                                    <Button type="submit" disabled={busy} data-testid={`${testid}-submit`} className="rounded-full">
                                        {busy ? "Saving…" : editing ? "Update" : "Save"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                }
            />

            <div className="px-8 py-8">
                {loading ? <LoadingBlock /> : items.length === 0 ? (
                    <EmptyState
                        icon={emptyIcon}
                        title={emptyTitle}
                        body={emptyBody}
                        testid={`${testid}-empty`}
                        action={
                            <Button onClick={startCreate} className="rounded-full" data-testid={`${testid}-empty-add`}>
                                <Plus className="mr-2 h-4 w-4" /> Add first entry
                            </Button>
                        }
                    />
                ) : (
                    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`${testid}-table`}>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {columns.map((c) => (
                                        <TableHead key={c.key} className="label-tag text-xs">{c.label}</TableHead>
                                    ))}
                                    <TableHead className="text-right label-tag text-xs">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((row) => (
                                    <TableRow key={row.id} data-testid={`row-${row.id}`}>
                                        {columns.map((c) => (
                                            <TableCell key={c.key}>
                                                {c.render ? c.render(row) : (row[c.key] ?? "—")}
                                            </TableCell>
                                        ))}
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => startEdit(row)} data-testid={`edit-${row.id}`}>
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => onDelete(row)} data-testid={`delete-${row.id}`}>
                                                <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}
